import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { addLookupValue } from '../../services/firebase';
import { useLookupCollection } from '../../hooks/useLookupCollection';
import { humanizeLookupCollection } from '../BrandPortal/RequestNewValueModal';

/**
 * ApproveLookupValueModal — Brand Portal Phase 2.5 (chunk v).
 *
 * Lightweight create form used by the admin approve flow for `lookup` requests.
 * The full `LookupValueEditor` is a routed page component (table + settings) and
 * doesn't surface the new value id cleanly, so the approve flow uses this focused
 * modal instead: it writes one value via `addLookupValue` (which returns the id)
 * and hands that id back so the caller can record it as the resolved entity.
 *
 * Pre-fills the display name + a slugified internal key from the requested value;
 * the admin reviews/tweaks before creating.
 */

interface Props {
  isOpen: boolean;
  /** Lookup collection slug, e.g. 'lookup_occasions'. */
  collection: string;
  /** The value the brand owner proposed (used to seed the form). */
  initialValue: string;
  /** Optional brand-owner note shown as context. */
  note?: string;
  /** Called with the new value id after a successful create. */
  onCreated: (newId: string) => void;
  onClose: () => void;
}

/** Slugify a display value into an internal key (matches lookup key conventions). */
function toKey(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function ApproveLookupValueModal({
  isOpen,
  collection,
  initialValue,
  note,
  onCreated,
  onClose,
}: Props) {
  // Used only to compute the next sortOrder from the live values.
  const { values } = useLookupCollection(collection);
  const [displayName, setDisplayName] = useState(initialValue);
  const [key, setKey] = useState(toKey(initialValue));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDisplayName(initialValue);
      setKey(toKey(initialValue));
    }
  }, [isOpen, initialValue]);

  const handleCreate = async () => {
    const keyTrim = key.trim();
    const nameTrim = displayName.trim();
    if (!keyTrim || !nameTrim) {
      toast.error('Value and display name are required');
      return;
    }
    setSaving(true);
    try {
      const nextSortOrder =
        values.length > 0
          ? Math.max(...values.map((v) => v.sortOrder ?? 0)) + 1
          : 1;
      const newId = await addLookupValue(collection, {
        value: keyTrim,
        displayName: nameTrim,
        isActive: true,
        sortOrder: nextSortOrder,
      });
      onCreated(newId);
    } catch {
      toast.error('Failed to create lookup value');
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add value · ${humanizeLookupCollection(collection)}`}
      size='md'
    >
      <div className='space-y-4'>
        {note && (
          <div className='rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 whitespace-pre-line'>
            Brand owner notes: {note}
          </div>
        )}
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Value (internal key)
          </label>
          <input
            type='text'
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            placeholder='e.g., stainless_steel'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Display Name
          </label>
          <input
            type='text'
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
            placeholder='e.g., Stainless Steel'
          />
        </div>
        <div className='flex justify-end gap-3 pt-2 border-t'>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            loading={saving}
            disabled={!key.trim() || !displayName.trim()}
          >
            Create &amp; approve
          </Button>
        </div>
      </div>
    </Modal>
  );
}
